import { useState } from "react";

const tools = [
  {
    icon: "ri-bank-card-line",
    color: "#2563EB",
    bg: "linear-gradient(135deg, #EFF6FF, #DBEAFE)",
    title: "Payments",
    desc: "Accept payments anywhere",
    hasDetail: true,
  },
  {
    icon: "ri-repeat-line",
    color: "#14B8A6",
    bg: "linear-gradient(135deg, #F0FDFA, #CCFBF1)",
    title: "Subscriptions",
    desc: "Bill customers recurring",
    hasDetail: true,
  },
  {
    icon: "ri-links-line",
    color: "#2563EB",
    bg: "linear-gradient(135deg, #F5F3FF, #EDE9FE)",
    title: "Connect",
    desc: "Build platforms & marketplaces",
    hasDetail: true,
  },
  {
    icon: "ri-send-plane-line",
    color: "#F59E0B",
    bg: "linear-gradient(135deg, #FFFBEB, #FEF3C7)",
    title: "Payouts",
    desc: "Send money worldwide",
    hasDetail: true,
  },
  {
    icon: "ri-radar-line",
    color: "#EF4444",
    bg: "linear-gradient(135deg, #FEF2F2, #FEE2E2)",
    title: "Radar",
    desc: "Fraud prevention that learns",
    hasDetail: true,
  },
  {
    icon: "ri-file-list-3-line",
    color: "#10B981",
    bg: "linear-gradient(135deg, #ECFDF5, #D1FAE5)",
    title: "Billing",
    desc: "Invoicing & collections",
    hasDetail: true,
  },
];

const paymentMethods = [
  { icon: "ri-bank-card-line", label: "Visa / Mastercard" },
  { icon: "ri-bank-card-2-line", label: "Debit Cards" },
  { icon: "ri-apple-line", label: "Apple Pay" },
  { icon: "ri-google-line", label: "Google Pay" },
  { icon: "ri-paypal-line", label: "PayPal" },
  { icon: "ri-wallet-3-line", label: "Local Wallets" },
];

const features = [
  {
    icon: "ri-flashlight-line",
    color: "#2563EB",
    title: "Smart retry logic",
    desc: "Automatically retries failed transactions using intelligent routing to maximize revenue recovery. Recovers up to 30% of failed payments.",
  },
  {
    icon: "ri-shield-keyhole-line",
    color: "#14B8A6",
    title: "Tokenization",
    desc: "Vault card data securely with PCI-compliant tokenization. Reuse tokens across transactions without storing raw card numbers.",
  },
  {
    icon: "ri-route-line",
    color: "#2563EB",
    title: "Intelligent routing",
    desc: "Route each transaction to the best-performing processor in real time. Higher approval rates, lower costs — automatically.",
  },
  {
    icon: "ri-global-line",
    color: "#14B8A6",
    title: "Local payment methods",
    desc: "Support for 135+ currencies and regional payment methods including SEPA, iDEAL, PIX, Boleto, and more.",
  },
  {
    icon: "ri-smartphone-line",
    color: "#2563EB",
    title: "Seamless checkout",
    desc: "Optimized checkout flows for web and mobile. One-click purchasing, saved cards, and address autofill built in.",
  },
  {
    icon: "ri-bar-chart-2-line",
    color: "#14B8A6",
    title: "Conversion optimization",
    desc: "Real-time analytics and A/B testing tools to identify drop-off and boost checkout conversion rates.",
  },
];

const stats = [
  { value: "99.99%", label: "Uptime SLA", icon: "ri-server-line" },
  { value: "<200ms", label: "Avg. latency", icon: "ri-timer-flash-line" },
  { value: "135+", label: "Currencies", icon: "ri-global-line" },
  { value: "+30%", label: "Recovery rate", icon: "ri-refresh-line" },
];

const subFeatures = [
  {
    icon: "ri-calendar-line",
    color: "#14B8A6",
    title: "Flexible billing cycles",
    desc: "Daily, weekly, monthly, annual, or fully custom billing intervals. Match any pricing model your SaaS or membership platform needs.",
  },
  {
    icon: "ri-scales-line",
    color: "#2563EB",
    title: "Proration & upgrades",
    desc: "Instantly calculate prorated charges when customers upgrade or downgrade mid-cycle. No manual math, no unhappy customers.",
  },
  {
    icon: "ri-gift-line",
    color: "#14B8A6",
    title: "Free trials & coupons",
    desc: "Launch trials without capturing payment upfront. Apply discount codes, percentage off, or fixed-amount coupons at checkout.",
  },
  {
    icon: "ri-arrow-up-down-line",
    color: "#2563EB",
    title: "Upgrade / downgrade",
    desc: "Let customers switch plans instantly with automatic billing adjustments, immediate access changes, and zero churn friction.",
  },
  {
    icon: "ri-mail-send-line",
    color: "#14B8A6",
    title: "Dunning management",
    desc: "Smart retry schedules and automated recovery emails nudge customers to update payment details before subscriptions lapse.",
  },
  {
    icon: "ri-bar-chart-line",
    color: "#2563EB",
    title: "Real-time revenue tracking",
    desc: "Live MRR, ARR, churn rate, and LTV dashboards. Know exactly how your subscription business is performing at any moment.",
  },
];

const subStats = [
  { value: "98.6%", label: "Recovery rate", icon: "ri-refresh-line" },
  { value: "<1min", label: "Plan switch time", icon: "ri-timer-line" },
  { value: "50+", label: "Billing models", icon: "ri-stack-line" },
  { value: "0 code", label: "Plan changes", icon: "ri-code-s-slash-line" },
];

const subBadges = [
  { icon: "ri-repeat-line", text: "Set-and-forget recurring billing" },
  { icon: "ri-shield-check-line", text: "PCI DSS Level 1 certified" },
  { icon: "ri-notification-line", text: "Automated dunning & recovery emails" },
  { icon: "ri-line-chart-line", text: "Live MRR & churn dashboards" },
];

const platforms = [
  { icon: "ri-code-box-line", label: "SaaS" },
  { icon: "ri-group-line", label: "Memberships" },
  { icon: "ri-newspaper-line", label: "Publishing" },
  { icon: "ri-store-line", label: "E-commerce" },
  { icon: "ri-graduation-cap-line", label: "EdTech" },
  { icon: "ri-heart-pulse-line", label: "Health & Fitness" },
];

const connectFeatures = [
  {
    icon: "ri-user-add-line",
    color: "#2563EB",
    title: "KYC onboarding",
    desc: "Automated identity verification and document checks. Onboard vendors, sellers, and service providers in minutes — not days.",
  },
  {
    icon: "ri-split-cells-horizontal",
    color: "#14B8A6",
    title: "Split payments",
    desc: "Automatically route funds between multiple parties in a single transaction. Set custom split rules, platform fees, and payout schedules.",
  },
  {
    icon: "ri-shield-user-line",
    color: "#2563EB",
    title: "Account verification",
    desc: "Real-time bank account and identity validation. Prevent fraud at onboarding before any money moves.",
  },
  {
    icon: "ri-send-plane-2-line",
    color: "#14B8A6",
    title: "Automated payouts",
    desc: "Schedule instant or batched payouts to vendors, gig workers, and partners. Support for 40+ countries and 135+ currencies.",
  },
  {
    icon: "ri-settings-4-line",
    color: "#2563EB",
    title: "Multi-party transactions",
    desc: "Manage complex fund flows across buyers, sellers, and your platform with full audit trails and configurable fee structures.",
  },
  {
    icon: "ri-bar-chart-box-line",
    color: "#14B8A6",
    title: "Platform dashboard",
    desc: "Unified view of all connected accounts, transaction volumes, pending payouts, and compliance status in real time.",
  },
];

const connectStats = [
  { value: "<5min", label: "KYC onboarding", icon: "ri-user-received-line" },
  { value: "40+", label: "Payout countries", icon: "ri-global-line" },
  { value: "0.1%", label: "Split precision", icon: "ri-percent-line" },
  { value: "Real-time", label: "Fund tracking", icon: "ri-radar-line" },
];

const connectUseCases = [
  { icon: "ri-car-line", label: "Gig platforms" },
  { icon: "ri-store-2-line", label: "Marketplaces" },
  { icon: "ri-team-line", label: "Partner ecosystems" },
  { icon: "ri-home-line", label: "Rental platforms" },
  { icon: "ri-service-line", label: "Freelance tools" },
  { icon: "ri-ticket-line", label: "Event platforms" },
];

const connectBadges = [
  { icon: "ri-user-add-line", text: "KYC verified in under 5 minutes" },
  { icon: "ri-shield-check-line", text: "AML & compliance built-in" },
  { icon: "ri-send-plane-line", text: "Instant payouts to 40+ countries" },
  { icon: "ri-eye-line", text: "Full audit trail on every transaction" },
];

const payoutsFeatures = [
  {
    icon: "ri-flashlight-line",
    color: "#F59E0B",
    title: "Instant payouts",
    desc: "Push funds to bank accounts, debit cards, and digital wallets within seconds — 24/7, including weekends and holidays.",
  },
  {
    icon: "ri-global-line",
    color: "#F59E0B",
    title: "Global coverage",
    desc: "Reach recipients in 60+ countries using local payment rails — ACH, SEPA, Faster Payments, PIX, and more. No SWIFT fees.",
  },
  {
    icon: "ri-calendar-schedule-line",
    color: "#F59E0B",
    title: "Scheduled payouts",
    desc: "Set daily, weekly, or monthly payout cycles. Automate vendor payments, freelancer wages, and partner disbursements on any schedule.",
  },
  {
    icon: "ri-radar-line",
    color: "#F59E0B",
    title: "Real-time tracking",
    desc: "Live status updates for every payout — pending, in transit, settled, or failed. Full visibility for you and your recipients.",
  },
  {
    icon: "ri-file-list-3-line",
    color: "#F59E0B",
    title: "Automated reconciliation",
    desc: "Auto-match every payout to its transaction record. Export clean reports for accounting, tax, and audits with one click.",
  },
  {
    icon: "ri-exchange-funds-line",
    color: "#F59E0B",
    title: "Multi-currency payouts",
    desc: "Pay in the recipient's local currency. Competitive FX rates, real-time conversion, and no hidden spread markups.",
  },
];

const payoutsStats = [
  { value: "<10s", label: "Avg. payout speed", icon: "ri-timer-flash-line" },
  { value: "60+", label: "Countries covered", icon: "ri-global-line" },
  { value: "135+", label: "Currencies", icon: "ri-money-dollar-circle-line" },
  { value: "99.97%", label: "Success rate", icon: "ri-checkbox-circle-line" },
];

const payoutsRails = [
  { icon: "ri-bank-line", label: "ACH" },
  { icon: "ri-bank-line", label: "SEPA" },
  { icon: "ri-flashlight-line", label: "Faster Payments" },
  { icon: "ri-leaf-line", label: "PIX" },
  { icon: "ri-bank-card-2-line", label: "Debit Push" },
  { icon: "ri-wallet-3-line", label: "Digital Wallets" },
];

const payoutsBadges = [
  { icon: "ri-flashlight-line", text: "Funds land in under 10 seconds" },
  { icon: "ri-global-line", text: "60+ countries, zero SWIFT fees" },
  { icon: "ri-file-chart-line", text: "Auto-reconciliation on every payout" },
  { icon: "ri-lock-line", text: "Bank-grade security & compliance" },
];

const radarFeatures = [
  {
    icon: "ri-brain-line",
    color: "#EF4444",
    title: "AI risk scoring",
    desc: "Every transaction receives a real-time risk score from 0–100. Our ML models are trained on billions of data points and update continuously.",
  },
  {
    icon: "ri-eye-line",
    color: "#EF4444",
    title: "Behavioral analysis",
    desc: "Detect anomalies in typing speed, mouse movement, session duration, and navigation patterns before a payment is even submitted.",
  },
  {
    icon: "ri-smartphone-line",
    color: "#EF4444",
    title: "Device fingerprinting",
    desc: "Identify suspicious devices across sessions and accounts using hardware signals, browser entropy, and IP reputation data.",
  },
  {
    icon: "ri-settings-3-line",
    color: "#EF4444",
    title: "Customizable rules",
    desc: "Write your own fraud rules or choose from a library of templates. Block, allow, or review transactions based on any combination of signals.",
  },
  {
    icon: "ri-shield-flash-line",
    color: "#EF4444",
    title: "Automated blocking",
    desc: "High-risk transactions are blocked instantly with zero manual review. Low-friction for good users, zero tolerance for bad actors.",
  },
  {
    icon: "ri-refresh-line",
    color: "#EF4444",
    title: "Always learning",
    desc: "Radar models retrain on new fraud patterns daily. The more transactions it sees, the smarter it gets — across all Paygent customers.",
  },
];

const radarStats = [
  { value: "99.8%", label: "Fraud catch rate", icon: "ri-shield-check-line" },
  { value: "<50ms", label: "Risk score latency", icon: "ri-timer-flash-line" },
  { value: "0.01%", label: "False positive rate", icon: "ri-emotion-happy-line" },
  { value: "10B+", label: "Signals analyzed daily", icon: "ri-database-2-line" },
];

const radarSignals = [
  { icon: "ri-map-pin-line", label: "Geolocation" },
  { icon: "ri-smartphone-line", label: "Device ID" },
  { icon: "ri-user-line", label: "Behavior" },
  { icon: "ri-global-line", label: "IP Reputation" },
  { icon: "ri-time-line", label: "Velocity" },
  { icon: "ri-links-line", label: "Network Graph" },
];

const radarBadges = [
  { icon: "ri-brain-line", text: "ML models retrain daily" },
  { icon: "ri-shield-check-line", text: "99.8% fraud catch rate" },
  { icon: "ri-flashlight-line", text: "Risk scored in under 50ms" },
  { icon: "ri-emotion-happy-line", text: "0.01% false positive rate" },
];

const riskLevels = [
  { label: "Low risk", range: "0–30", color: "#10B981", bg: "#ECFDF5", width: "30%" },
  { label: "Medium risk", range: "31–70", color: "#F59E0B", bg: "#FFFBEB", width: "40%" },
  { label: "High risk", range: "71–100", color: "#EF4444", bg: "#FEF2F2", width: "30%" },
];

const billingFeatures = [
  {
    icon: "ri-file-text-line",
    color: "#10B981",
    title: "Custom invoices",
    desc: "Create branded, itemized invoices in seconds. Add your logo, line items, discounts, and custom fields — fully white-labeled for your business.",
  },
  {
    icon: "ri-links-line",
    color: "#10B981",
    title: "Payment links",
    desc: "Generate shareable payment links for any invoice. Customers pay instantly via card, wallet, or bank transfer — no login required.",
  },
  {
    icon: "ri-alarm-line",
    color: "#10B981",
    title: "Automated reminders",
    desc: "Schedule smart follow-up emails before and after due dates. Reduce late payments without lifting a finger.",
  },
  {
    icon: "ri-government-line",
    color: "#10B981",
    title: "Tax handling",
    desc: "Auto-calculate VAT, GST, and sales tax based on customer location. Stay compliant globally without the manual overhead.",
  },
  {
    icon: "ri-bar-chart-box-line",
    color: "#10B981",
    title: "Revenue reporting",
    desc: "Track total billed, collected, and outstanding balances with real-time dashboards. Export detailed reports for accounting and audits.",
  },
  {
    icon: "ri-building-4-line",
    color: "#10B981",
    title: "Enterprise-ready",
    desc: "Bulk invoice generation, multi-entity support, approval workflows, and role-based access for finance teams of any size.",
  },
];

const billingStats = [
  { value: "92%", label: "On-time payment rate", icon: "ri-checkbox-circle-line" },
  { value: "-68%", label: "Overdue invoices", icon: "ri-arrow-down-line" },
  { value: "50+", label: "Tax jurisdictions", icon: "ri-government-line" },
  { value: "<30s", label: "Invoice creation", icon: "ri-timer-line" },
];

const billingUsedBy = [
  { icon: "ri-store-line", label: "Small business" },
  { icon: "ri-briefcase-line", label: "Agencies" },
  { icon: "ri-building-line", label: "Enterprise" },
  { icon: "ri-code-box-line", label: "SaaS" },
  { icon: "ri-user-star-line", label: "Freelancers" },
  { icon: "ri-hospital-line", label: "Professional services" },
];

const billingBadges = [
  { icon: "ri-file-text-line", text: "Invoices created in under 30 seconds" },
  { icon: "ri-alarm-line", text: "Auto-reminders cut overdue by 68%" },
  { icon: "ri-government-line", text: "Tax-compliant in 50+ jurisdictions" },
  { icon: "ri-shield-check-line", text: "SOC 2 Type II & GDPR compliant" },
];

const sampleInvoice = [
  { item: "Design services", qty: 1, price: "$4,200.00" },
  { item: "Hosting (annual)", qty: 1, price: "$1,560.00" },
  { item: "Support retainer", qty: 3, price: "$900.00" },
];

function BillingModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.18)" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white px-8 pt-8 pb-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 w-4 h-4 flex items-center justify-center" />
          </button>
          <div className="flex items-center gap-4 mb-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #ECFDF5, #D1FAE5)" }}>
              <i className="ri-file-list-3-line text-xl w-6 h-6 flex items-center justify-center" style={{ color: "#10B981" }} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <h2 className="text-xl font-bold text-gray-900">Billing</h2>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-emerald-700 bg-emerald-50">Invoicing &amp; Collections</span>
              </div>
              <p className="text-sm text-gray-500">From your first invoice to enterprise-scale collections — all in one place</p>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-8">
          {/* Hero blurb */}
          <div>
            <p className="text-gray-700 leading-relaxed text-sm">
              Paygent Billing lets you{" "}
              <strong className="text-gray-900">create, send, and manage invoices</strong>{" "}
              with zero friction. Custom invoices, one-click payment links, automated reminders, and built-in tax handling — with{" "}
              <strong className="text-gray-900">real-time reporting</strong>{" "}
              so you always know what’s been paid, what’s pending, and what needs chasing. Built for freelancers and enterprise finance teams alike.
            </p>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {billingStats.map((s) => (
              <div key={s.label} className="flex flex-col items-center text-center p-3 rounded-xl bg-gray-50">
                <i className={`${s.icon} text-lg mb-1 w-5 h-5 flex items-center justify-center`} style={{ color: "#10B981" }} />
                <div className="text-lg font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Sample invoice preview */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Sample Invoice Preview</h3>
            <div className="rounded-2xl border border-gray-100 overflow-hidden">
              <div className="px-5 py-4 flex items-center justify-between"
                style={{ background: "linear-gradient(135deg, #ECFDF5, #D1FAE5)" }}>
                <div>
                  <div className="text-sm font-bold text-gray-900">Invoice #INV-2025-0094</div>
                  <div className="text-xs text-gray-500 mt-0.5">Due: May 15, 2025</div>
                </div>
                <span className="text-xs font-bold px-2.5 py-1 rounded-full text-amber-700 bg-amber-50">Awaiting payment</span>
              </div>
              <div className="divide-y divide-gray-50">
                {sampleInvoice.map((row) => (
                  <div key={row.item} className="flex items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-2">
                      <i className="ri-checkbox-blank-circle-line w-3 h-3 flex items-center justify-center" style={{ color: "#10B981" }} />
                      <span className="text-sm text-gray-700">{row.item}</span>
                    </div>
                    <div className="flex items-center gap-6">
                      <span className="text-xs text-gray-400">x{row.qty}</span>
                      <span className="text-sm font-semibold text-gray-800">{row.price}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div className="px-5 py-3 bg-gray-50 flex items-center justify-between border-t border-gray-100">
                <span className="text-sm font-bold text-gray-900">Total</span>
                <span className="text-sm font-bold text-emerald-600">$6,660.00</span>
              </div>
            </div>
          </div>

          {/* Used by */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Used by every type of business</h3>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {billingUsedBy.map((u) => (
                <div key={u.label} className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border border-gray-100 bg-white">
                  <i className={`${u.icon} text-lg w-5 h-5 flex items-center justify-center`} style={{ color: "#10B981" }} />
                  <span className="text-xs text-gray-500 text-center leading-tight">{u.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Features grid */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Core Features</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {billingFeatures.map((f) => (
                <div key={f.title} className="flex gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #ECFDF5, #D1FAE5)" }}>
                    <i className={`${f.icon} text-sm w-4 h-4 flex items-center justify-center`} style={{ color: f.color }} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-800 mb-0.5">{f.title}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Microcopy badges */}
          <div className="flex flex-wrap gap-2">
            {billingBadges.map((b) => (
              <div key={b.text} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-100 bg-white text-xs text-gray-600 font-medium">
                <i className={`${b.icon} w-3.5 h-3.5 flex items-center justify-center`} style={{ color: "#10B981" }} />
                <span>{b.text}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-gray-100">
            <button
              onClick={onClose}
              className="flex-1 py-3 text-sm font-bold rounded-xl text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #10B981, #059669)" }}
            >
              Send your first invoice
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-3 text-sm font-semibold rounded-xl text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              View billing docs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RadarModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.18)" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white px-8 pt-8 pb-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 w-4 h-4 flex items-center justify-center" />
          </button>
          <div className="flex items-center gap-4 mb-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #FEF2F2, #FEE2E2)" }}>
              <i className="ri-radar-line text-xl w-6 h-6 flex items-center justify-center" style={{ color: "#EF4444" }} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <h2 className="text-xl font-bold text-gray-900">Radar</h2>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-red-700 bg-red-50">AI-Powered</span>
              </div>
              <p className="text-sm text-gray-500">Intelligent fraud detection that adapts in real time — always learning, never sleeping</p>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-8">
          {/* Hero blurb */}
          <div>
            <p className="text-gray-700 leading-relaxed text-sm">
              Paygent Radar uses{" "}
              <strong className="text-gray-900">AI-powered fraud detection</strong>{" "}
              to monitor every transaction in real time. By analyzing behavioral patterns, device signals, and transaction data, Radar stops fraud before it happens — with{" "}
              <strong className="text-gray-900">customizable rules, dynamic risk scoring, and automated blocking</strong>{" "}
              that gets smarter with every payment processed.
            </p>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {radarStats.map((s) => (
              <div key={s.label} className="flex flex-col items-center text-center p-3 rounded-xl bg-gray-50">
                <i className={`${s.icon} text-lg mb-1 w-5 h-5 flex items-center justify-center`} style={{ color: "#EF4444" }} />
                <div className="text-lg font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Risk score visualizer */}
          <div className="rounded-2xl p-5" style={{ background: "linear-gradient(135deg, #FEF2F2, #FEE2E2)" }}>
            <div className="text-xs font-bold text-red-800 uppercase tracking-wide mb-3">Real-time risk score breakdown</div>
            <div className="space-y-2 mb-3">
              {riskLevels.map((level) => (
                <div key={level.label} className="flex items-center gap-3">
                  <div className="w-24 text-xs font-medium text-gray-700 whitespace-nowrap">{level.label}</div>
                  <div className="flex-1 h-5 rounded-full bg-white overflow-hidden">
                    <div
                      className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-700"
                      style={{ width: level.width, background: level.color }}
                    >
                      <span className="text-white text-xs font-bold">{level.range}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-xs text-red-700">Transactions scored 71+ are auto-blocked. Scores 31–70 trigger 3D Secure review.</p>
          </div>

          {/* Signals grid */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Signals Analyzed per Transaction</h3>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {radarSignals.map((s) => (
                <div key={s.label} className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border border-gray-100 bg-white">
                  <i className={`${s.icon} text-lg w-5 h-5 flex items-center justify-center`} style={{ color: "#EF4444" }} />
                  <span className="text-xs text-gray-500 text-center leading-tight">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Features grid */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Core Features</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {radarFeatures.map((f) => (
                <div key={f.title} className="flex gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #FEF2F2, #FEE2E2)" }}>
                    <i className={`${f.icon} text-sm w-4 h-4 flex items-center justify-center`} style={{ color: f.color }} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-800 mb-0.5">{f.title}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Microcopy badges */}
          <div className="flex flex-wrap gap-2">
            {radarBadges.map((b) => (
              <div key={b.text} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-100 bg-white text-xs text-gray-600 font-medium">
                <i className={`${b.icon} w-3.5 h-3.5 flex items-center justify-center`} style={{ color: "#EF4444" }} />
                <span>{b.text}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-gray-100">
            <button
              onClick={onClose}
              className="flex-1 py-3 text-sm font-bold rounded-xl text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #EF4444, #DC2626)" }}
            >
              Activate Radar protection
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-3 text-sm font-semibold rounded-xl text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              View rule builder
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PayoutsModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.18)" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white px-8 pt-8 pb-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 w-4 h-4 flex items-center justify-center" />
          </button>
          <div className="flex items-center gap-4 mb-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #FFFBEB, #FEF3C7)" }}>
              <i className="ri-send-plane-line text-xl w-6 h-6 flex items-center justify-center" style={{ color: "#F59E0B" }} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <h2 className="text-xl font-bold text-gray-900">Payouts</h2>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-amber-700 bg-amber-50">Global &amp; Instant</span>
              </div>
              <p className="text-sm text-gray-500">Fast, reliable disbursements to anyone, anywhere in the world</p>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-8">
          {/* Hero blurb */}
          <div>
            <p className="text-gray-700 leading-relaxed text-sm">
              Paygent Payouts enables{" "}
              <strong className="text-gray-900">fast and flexible disbursements</strong>{" "}
              to users, vendors, and businesses globally. From instant bank transfers to local payment rails, every payout is tracked in real time with{" "}
              <strong className="text-gray-900">automated reconciliation</strong>{" "}
              built in. No delays, no manual work, no surprises.
            </p>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {payoutsStats.map((s) => (
              <div key={s.label} className="flex flex-col items-center text-center p-3 rounded-xl bg-gray-50">
                <i className={`${s.icon} text-lg mb-1 w-5 h-5 flex items-center justify-center`} style={{ color: "#F59E0B" }} />
                <div className="text-lg font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Payment rails */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Supported Payment Rails</h3>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {payoutsRails.map((r) => (
                <div key={r.label} className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border border-gray-100 bg-white">
                  <i className={`${r.icon} text-lg w-5 h-5 flex items-center justify-center`} style={{ color: "#F59E0B" }} />
                  <span className="text-xs text-gray-500 text-center leading-tight">{r.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Features grid */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Core Features</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {payoutsFeatures.map((f) => (
                <div key={f.title} className="flex gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #FFFBEB, #FEF3C7)" }}>
                    <i className={`${f.icon} text-sm w-4 h-4 flex items-center justify-center`} style={{ color: f.color }} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-800 mb-0.5">{f.title}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Payout journey visual */}
          <div className="rounded-2xl p-5" style={{ background: "linear-gradient(135deg, #FFFBEB, #FEF3C7)" }}>
            <div className="text-xs font-bold text-amber-800 uppercase tracking-wide mb-3">A payout in motion</div>
            <div className="flex items-center gap-1 flex-wrap">
              {[
                { label: "Payout triggered", icon: "ri-cursor-line", color: "#F59E0B" },
                { label: "Compliance check", icon: "ri-shield-check-line", color: "#F59E0B" },
                { label: "Rail selected", icon: "ri-route-line", color: "#F59E0B" },
                { label: "Funds dispatched", icon: "ri-send-plane-line", color: "#F59E0B" },
                { label: "Settled &lt;10s", icon: "ri-checkbox-circle-line", color: "#10B981" },
              ].map((step, i, arr) => (
                <div key={step.label} className="flex items-center gap-1">
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white">
                    <i className={`${step.icon} w-3.5 h-3.5 flex items-center justify-center`} style={{ color: step.color }} />
                    <span className="text-xs font-medium text-gray-700 whitespace-nowrap" dangerouslySetInnerHTML={{ __html: step.label }} />
                  </div>
                  {i < arr.length - 1 && (
                    <i className="ri-arrow-right-s-line text-amber-400 w-4 h-4 flex items-center justify-center" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Microcopy badges */}
          <div className="flex flex-wrap gap-2">
            {payoutsBadges.map((b) => (
              <div key={b.text} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-100 bg-white text-xs text-gray-600 font-medium">
                <i className={`${b.icon} w-3.5 h-3.5 flex items-center justify-center`} style={{ color: "#F59E0B" }} />
                <span>{b.text}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-gray-100">
            <button
              onClick={onClose}
              className="flex-1 py-3 text-sm font-bold rounded-xl text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #F59E0B, #D97706)" }}
            >
              Start sending payouts
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-3 text-sm font-semibold rounded-xl text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              View coverage map
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConnectModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.18)" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white px-8 pt-8 pb-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 w-4 h-4 flex items-center justify-center" />
          </button>
          <div className="flex items-center gap-4 mb-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #F5F3FF, #EDE9FE)" }}>
              <i className="ri-links-line text-xl w-6 h-6 flex items-center justify-center" style={{ color: "#2563EB" }} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <h2 className="text-xl font-bold text-gray-900">Connect</h2>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-indigo-700 bg-indigo-50">Platforms &amp; Marketplaces</span>
              </div>
              <p className="text-sm text-gray-500">Multi-party payments infrastructure for the modern platform economy</p>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-8">
          {/* Hero blurb */}
          <div>
            <p className="text-gray-700 leading-relaxed text-sm">
              Paygent Connect lets platforms and marketplaces{" "}
              <strong className="text-gray-900">onboard users, split payments, and manage multi-party transactions</strong>{" "}
              at scale. From gig workers to vendor networks, Connect handles the full financial stack — KYC, compliance, fund splitting, and automated payouts — so you can focus on building.
            </p>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {connectStats.map((s) => (
              <div key={s.label} className="flex flex-col items-center text-center p-3 rounded-xl bg-gray-50">
                <i className={`${s.icon} text-lg mb-1 w-5 h-5 flex items-center justify-center`} style={{ color: "#2563EB" }} />
                <div className="text-lg font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Use cases */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Built for these platforms</h3>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {connectUseCases.map((u) => (
                <div key={u.label} className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border border-gray-100 bg-white">
                  <i className={`${u.icon} text-lg w-5 h-5 flex items-center justify-center`} style={{ color: "#2563EB" }} />
                  <span className="text-xs text-gray-500 text-center leading-tight">{u.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Features grid */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Core Features</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {connectFeatures.map((f) => (
                <div key={f.title} className="flex gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ background: f.color === "#2563EB" ? "linear-gradient(135deg,#EFF6FF,#DBEAFE)" : "linear-gradient(135deg,#F0FDFA,#CCFBF1)" }}>
                    <i className={`${f.icon} text-sm w-4 h-4 flex items-center justify-center`} style={{ color: f.color }} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-800 mb-0.5">{f.title}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Money flow visual */}
          <div className="rounded-2xl p-5" style={{ background: "linear-gradient(135deg, #F5F3FF, #EDE9FE)" }}>
            <div className="text-xs font-bold text-indigo-800 uppercase tracking-wide mb-3">How a split payment works</div>
            <div className="flex items-center gap-1 flex-wrap">
              {[
                { label: "Customer pays", icon: "ri-bank-card-line", color: "#2563EB" },
                { label: "Platform fee deducted", icon: "ri-percent-line", color: "#2563EB" },
                { label: "Funds split", icon: "ri-split-cells-horizontal", color: "#14B8A6" },
                { label: "Vendors paid out", icon: "ri-send-plane-line", color: "#14B8A6" },
                { label: "Settled instantly", icon: "ri-checkbox-circle-line", color: "#10B981" },
              ].map((step, i, arr) => (
                <div key={step.label} className="flex items-center gap-1">
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white">
                    <i className={`${step.icon} w-3.5 h-3.5 flex items-center justify-center`} style={{ color: step.color }} />
                    <span className="text-xs font-medium text-gray-700 whitespace-nowrap">{step.label}</span>
                  </div>
                  {i < arr.length - 1 && (
                    <i className="ri-arrow-right-s-line text-indigo-400 w-4 h-4 flex items-center justify-center" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Microcopy badges */}
          <div className="flex flex-wrap gap-2">
            {connectBadges.map((b) => (
              <div key={b.text} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-100 bg-white text-xs text-gray-600 font-medium">
                <i className={`${b.icon} w-3.5 h-3.5 flex items-center justify-center`} style={{ color: "#2563EB" }} />
                <span>{b.text}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-gray-100">
            <button
              onClick={onClose}
              className="flex-1 py-3 text-sm font-bold rounded-xl text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #2563EB, #14B8A6)" }}
            >
              Start building your platform
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-3 text-sm font-semibold rounded-xl text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              View docs
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function SubscriptionsModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.18)" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white px-8 pt-8 pb-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 w-4 h-4 flex items-center justify-center" />
          </button>
          <div className="flex items-center gap-4 mb-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #F0FDFA, #CCFBF1)" }}>
              <i className="ri-repeat-line text-xl w-6 h-6 flex items-center justify-center" style={{ color: "#14B8A6" }} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <h2 className="text-xl font-bold text-gray-900">Subscriptions</h2>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-teal-700 bg-teal-50">SaaS &amp; Memberships</span>
              </div>
              <p className="text-sm text-gray-500">Recurring revenue infrastructure — built to grow with you</p>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-8">
          {/* Hero blurb */}
          <div>
            <p className="text-gray-700 leading-relaxed text-sm">
              Paygent Subscriptions gives you everything to run{" "}
              <strong className="text-gray-900">recurring billing at scale</strong>. Flexible cycles, smart proration, automated dunning, and{" "}
              <strong className="text-gray-900">real-time revenue analytics</strong> — all in one platform. Built for SaaS teams and membership platforms that can&apos;t afford churn.
            </p>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {subStats.map((s) => (
              <div key={s.label} className="flex flex-col items-center text-center p-3 rounded-xl bg-gray-50">
                <i className={`${s.icon} text-lg mb-1 w-5 h-5 flex items-center justify-center`} style={{ color: "#14B8A6" }} />
                <div className="text-lg font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Built for platforms */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Built for your platform</h3>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {platforms.map((p) => (
                <div key={p.label} className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border border-gray-100 bg-white">
                  <i className={`${p.icon} text-lg w-5 h-5 flex items-center justify-center`} style={{ color: "#14B8A6" }} />
                  <span className="text-xs text-gray-500 text-center leading-tight">{p.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Features grid */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Core Features</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {subFeatures.map((f) => (
                <div key={f.title} className="flex gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ background: f.color === "#14B8A6" ? "linear-gradient(135deg,#F0FDFA,#CCFBF1)" : "linear-gradient(135deg,#EFF6FF,#DBEAFE)" }}>
                    <i className={`${f.icon} text-sm w-4 h-4 flex items-center justify-center`} style={{ color: f.color }} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-800 mb-0.5">{f.title}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dunning flow visual */}
          <div className="rounded-2xl p-5" style={{ background: "linear-gradient(135deg, #F0FDFA, #CCFBF1)" }}>
            <div className="text-xs font-bold text-teal-800 uppercase tracking-wide mb-3">Failed payment recovery flow</div>
            <div className="flex items-center gap-1 flex-wrap">
              {[
                { label: "Payment fails", icon: "ri-close-circle-line", color: "#EF4444" },
                { label: "Auto-retry (day 1)", icon: "ri-refresh-line", color: "#14B8A6" },
                { label: "Email nudge (day 3)", icon: "ri-mail-line", color: "#14B8A6" },
                { label: "Final retry (day 7)", icon: "ri-refresh-line", color: "#14B8A6" },
                { label: "Recovered!", icon: "ri-checkbox-circle-line", color: "#10B981" },
              ].map((step, i, arr) => (
                <div key={step.label} className="flex items-center gap-1">
                  <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-white">
                    <i className={`${step.icon} w-3.5 h-3.5 flex items-center justify-center`} style={{ color: step.color }} />
                    <span className="text-xs font-medium text-gray-700 whitespace-nowrap">{step.label}</span>
                  </div>
                  {i < arr.length - 1 && (
                    <i className="ri-arrow-right-s-line text-teal-400 w-4 h-4 flex items-center justify-center" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Microcopy badges */}
          <div className="flex flex-wrap gap-2">
            {subBadges.map((b) => (
              <div key={b.text} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-100 bg-white text-xs text-gray-600 font-medium">
                <i className={`${b.icon} w-3.5 h-3.5 flex items-center justify-center`} style={{ color: "#14B8A6" }} />
                <span>{b.text}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-gray-100">
            <button
              onClick={onClose}
              className="flex-1 py-3 text-sm font-bold rounded-xl text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #14B8A6, #0D9488)" }}
            >
              Start billing subscribers
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-3 text-sm font-semibold rounded-xl text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              View pricing
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PaymentsModal({ onClose }: { onClose: () => void }) {
  const handleLearnMore = () => {
    onClose();
    setTimeout(() => {
      const el = document.getElementById("features-section");
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 200);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="relative bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        style={{ boxShadow: "0 24px 80px rgba(0,0,0,0.18)" }}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white px-8 pt-8 pb-4 border-b border-gray-100">
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-gray-500 w-4 h-4 flex items-center justify-center" />
          </button>
          <div className="flex items-center gap-4 mb-3">
            <div className="w-12 h-12 flex items-center justify-center rounded-2xl flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #EFF6FF, #DBEAFE)" }}>
              <i className="ri-bank-card-line text-xl w-6 h-6 flex items-center justify-center" style={{ color: "#2563EB" }} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <h2 className="text-xl font-bold text-gray-900">Payments</h2>
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full text-green-700 bg-green-50">Live globally</span>
              </div>
              <p className="text-sm text-gray-500">Powered by Paygent — built for speed, reliability, and conversion</p>
            </div>
          </div>
        </div>

        <div className="px-8 py-6 space-y-8">
          {/* Hero blurb */}
          <div>
            <p className="text-gray-700 leading-relaxed text-sm">
              Paygent enables businesses to accept payments globally with{" "}
              <strong className="text-gray-900">high approval rates</strong> and{" "}
              <strong className="text-gray-900">intelligent routing</strong>. Support credit cards, debit cards, digital wallets, and local payment methods — all through a single integration. Built for speed at every step of the checkout flow.
            </p>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {stats.map((s) => (
              <div key={s.label} className="flex flex-col items-center text-center p-3 rounded-xl bg-gray-50">
                <i className={`${s.icon} text-lg mb-1 w-5 h-5 flex items-center justify-center`} style={{ color: "#2563EB" }} />
                <div className="text-lg font-bold text-gray-900">{s.value}</div>
                <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>

          {/* Payment methods */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Accepted Payment Methods</h3>
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {paymentMethods.map((m) => (
                <div key={m.label} className="flex flex-col items-center gap-1.5 p-2.5 rounded-xl border border-gray-100 bg-white">
                  <i className={`${m.icon} text-lg w-5 h-5 flex items-center justify-center`} style={{ color: "#2563EB" }} />
                  <span className="text-xs text-gray-500 text-center leading-tight">{m.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Features grid */}
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wide">Core Features</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {features.map((f) => (
                <div key={f.title} className="flex gap-3 p-4 rounded-xl border border-gray-100 bg-gray-50/50 hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg flex-shrink-0"
                    style={{ background: f.color === "#2563EB" ? "linear-gradient(135deg,#EFF6FF,#DBEAFE)" : "linear-gradient(135deg,#F0FDFA,#CCFBF1)" }}>
                    <i className={`${f.icon} text-sm w-4 h-4 flex items-center justify-center`} style={{ color: f.color }} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-800 mb-0.5">{f.title}</div>
                    <div className="text-xs text-gray-500 leading-relaxed">{f.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Microcopy badges */}
          <div className="flex flex-wrap gap-2">
            {[
              { icon: "ri-flashlight-line", text: "Transactions cleared in &lt;200ms" },
              { icon: "ri-shield-check-line", text: "PCI DSS Level 1 certified" },
              { icon: "ri-refresh-line", text: "Auto-retry recovers lost revenue" },
              { icon: "ri-line-chart-line", text: "Real-time conversion insights" },
            ].map((b) => (
              <div key={b.text} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-100 bg-white text-xs text-gray-600 font-medium">
                <i className={`${b.icon} w-3.5 h-3.5 flex items-center justify-center`} style={{ color: "#2563EB" }} />
                <span dangerouslySetInnerHTML={{ __html: b.text }} />
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-gray-100">
            <button
              onClick={handleLearnMore}
              className="flex-1 py-3 text-sm font-bold rounded-xl text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap"
              style={{ background: "linear-gradient(135deg, #2563EB, #14B8A6)" }}
            >
              See Payments in action
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-3 text-sm font-semibold rounded-xl text-gray-700 border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              Explore all products
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ToolsSection() {
  const [showPaymentsModal, setShowPaymentsModal] = useState(false);
  const [showSubscriptionsModal, setShowSubscriptionsModal] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showPayoutsModal, setShowPayoutsModal] = useState(false);
  const [showRadarModal, setShowRadarModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);

  const handleCardClick = (title: string) => {
    if (title === "Payments") setShowPaymentsModal(true);
    if (title === "Subscriptions") setShowSubscriptionsModal(true);
    if (title === "Connect") setShowConnectModal(true);
    if (title === "Payouts") setShowPayoutsModal(true);
    if (title === "Radar") setShowRadarModal(true);
    if (title === "Billing") setShowBillingModal(true);
  };

  return (
    <>
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">Everything you need to scale</h2>
            <p className="text-lg text-gray-500">A complete suite of tools to power your payments infrastructure.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {tools.map((tool) => (
              <div
                key={tool.title}
                onClick={() => handleCardClick(tool.title)}
                className={`flex flex-col items-center text-center p-5 rounded-2xl border border-gray-100 bg-white hover:shadow-md transition-all duration-300 hover:-translate-y-1 group relative ${tool.hasDetail ? "cursor-pointer" : "cursor-default"}`}
                style={{ boxShadow: "0 1px 8px rgba(0,0,0,0.04)" }}
              >
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl mb-3 transition-transform duration-300 group-hover:scale-110"
                  style={{ background: tool.bg }}
                >
                  <i className={`${tool.icon} text-xl w-6 h-6 flex items-center justify-center`} style={{ color: tool.color }} />
                </div>
                <div className="text-sm font-bold text-gray-800 mb-1">{tool.title}</div>
                <div className="text-xs text-gray-400 leading-relaxed">{tool.desc}</div>
                {tool.hasDetail && (
                  <div className="absolute top-2.5 right-2.5 w-5 h-5 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: "linear-gradient(135deg,#EFF6FF,#DBEAFE)" }}>
                    <i className="ri-arrow-right-up-line text-xs w-3 h-3 flex items-center justify-center" style={{ color: "#2563EB" }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {showPaymentsModal && <PaymentsModal onClose={() => setShowPaymentsModal(false)} />}
      {showSubscriptionsModal && <SubscriptionsModal onClose={() => setShowSubscriptionsModal(false)} />}
      {showConnectModal && <ConnectModal onClose={() => setShowConnectModal(false)} />}
      {showPayoutsModal && <PayoutsModal onClose={() => setShowPayoutsModal(false)} />}
      {showRadarModal && <RadarModal onClose={() => setShowRadarModal(false)} />}
      {showBillingModal && <BillingModal onClose={() => setShowBillingModal(false)} />}
    </>
  );
}
